import { useNavigate } from 'react-router-dom'
import { Grid3X3, Users, Link2, Shuffle, AlertCircle } from 'lucide-react'
import { useBlindSpots } from '@/hooks/useBlindSpots'
import type { BlindSpot } from '@/types'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'

const spotIcons: Record<BlindSpot['type'], React.ReactNode> = {
  domain_gap: <Grid3X3 className="w-4 h-4" />,
  depth_gap: <Users className="w-4 h-4" />,
  connection_gap: <Link2 className="w-4 h-4" />,
  contradiction: <Shuffle className="w-4 h-4" />,
}

const impactVariants: Record<string, string> = {
  high: 'bg-red-500/20 text-red-400 border-red-500/20',
  medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/20',
  low: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/20',
}

const impactLabels: Record<string, string> = {
  high: 'Alto',
  medium: 'Medio',
  low: 'Baixo',
}

export default function BlindSpots() {
  const { data: spots, loading, error } = useBlindSpots()
  const navigate = useNavigate()

  if (loading) {
    return (
      <Card className="card-premium">
        <h3 className="text-header mb-6 flex items-center gap-3">
          <AlertCircle className="w-4 h-4 text-yellow-400" />
          Pontos Cegos
        </h3>
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-16 rounded-2xl" />
          ))}
        </div>
      </Card>
    )
  }

  if (error || !spots) {
    return null
  }

  if (spots.length === 0) {
    return (
      <Card className="card-premium">
        <h3 className="text-header mb-6 flex items-center gap-3">
          <AlertCircle className="w-4 h-4 text-yellow-400" />
          Pontos Cegos
        </h3>
        <div className="text-center py-8">
          <div className="empty-state-icon mx-auto">
            <span>&#10024;</span>
          </div>
          <p className="text-sm text-rsb-text-secondary">Nenhum ponto cego detectado!</p>
          <p className="text-xs text-rsb-muted mt-1.5">Seu conhecimento esta bem distribuido.</p>
        </div>
      </Card>
    )
  }

  return (
    <Card className="card-premium">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-header flex items-center gap-3">
          <AlertCircle className="w-4 h-4 text-yellow-400" />
          Pontos Cegos
          <Badge variant="outline" className="px-2 py-0.5 bg-yellow-500/15 text-yellow-400 text-[11px] border-yellow-500/20 font-semibold tabular-nums">
            {spots.length}
          </Badge>
        </h3>
      </div>

      <div className="space-y-3">
        {spots.slice(0, 5).map((spot, index) => (
          <div
            key={index}
            className="p-3 rounded-2xl border border-white/[0.08] hover:border-white/[0.16] transition-colors"
          >
            <div className="flex items-start gap-3">
              <div className="text-xl mt-0.5">{spotIcons[spot.type]}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="text-sm font-medium text-rsb-text truncate">{spot.title}</h4>
                  <Badge variant="outline" className={`text-xs ${impactVariants[spot.impact]}`}>
                    {impactLabels[spot.impact]}
                  </Badge>
                </div>
                <p className="text-xs text-rsb-text-secondary leading-relaxed">{spot.description}</p>
                {spot.suggestion && (
                  <p className="text-xs text-rsb-muted mt-1 italic">{spot.suggestion}</p>
                )}
                {spot.related_nodes && spot.related_nodes.length > 0 && (
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => navigate(`/dive/${spot.related_nodes![0]}`)}
                    className="mt-2 text-xs text-rsb-accent hover:underline p-0 h-auto"
                  >
                    Ver conceito →
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {spots.length > 5 && (
        <div className="mt-3 text-center">
          <Button
            variant="link"
            size="sm"
            onClick={() => navigate('/constellation')}
            className="text-xs text-rsb-accent hover:underline"
          >
            Ver mais {spots.length - 5} pontos cegos na Constellation →
          </Button>
        </div>
      )}
    </Card>
  )
}
